import type { ConditionGroup } from './automation.js';
import type { SavedView } from './filter.js';
import type { NotifyPrefs } from './notify.js';
import type { CardTemplate } from './templates.js';
import type { CardSeries } from './series.js';
export const ROLES = ['viewer', 'commenter', 'editor', 'owner'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  viewer: 'Betrachter',
  commenter: 'Kommentierer',
  editor: 'Bearbeiter',
  owner: 'Besitzer',
};

export function roleAtLeast(role: Role | undefined | null, min: Role): boolean {
  if (!role) return false;
  return ROLES.indexOf(role) >= ROLES.indexOf(min);
}

export const PRIORITIES = [
  { value: 0, label: 'Keine' },
  { value: 1, label: 'Niedrig' },
  { value: 2, label: 'Mittel' },
  { value: 3, label: 'Hoch' },
  { value: 4, label: 'Dringend' },
] as const;

export const LABEL_COLORS = [
  'green',
  'yellow',
  'orange',
  'red',
  'purple',
  'blue',
  'sky',
  'lime',
  'pink',
  'gray',
] as const;
export type LabelColor = (typeof LABEL_COLORS)[number];
/** 0.51: Spaltenfarbe – Labelfarbe, Markenfarbe des Boards (folgt dem Erscheinungsbild) oder eigener HEX-Wert */
export type ColumnColor = LabelColor | 'brand' | `#${string}`;
export const isLabelColor = (c: string): c is LabelColor => (LABEL_COLORS as readonly string[]).includes(c);

export const DEFAULT_COLUMNS = ['Offen', 'In Arbeit', 'Prüfung', 'Erledigt'] as const;

export type CoverSize = 'half' | 'full';

export interface Cover {
  type: 'image' | 'color';
  /** Attachment-ID bei Bild-Cover */
  attachmentId?: string | null;
  color?: LabelColor | null;
  size: CoverSize;
  /** Fokuspunkt 0..1 */
  focusX: number;
  focusY: number;
  alt?: string | null;
}

/** Cover mit aufgelösten Bild-URLs, wie es der Client bekommt */
export interface ResolvedCover extends Cover {
  url?: string | null;
  srcSet?: string | null;
  placeholder?: string | null;
  width?: number | null;
  height?: number | null;
}

export interface UserLite {
  id: string;
  name: string;
  email: string;
  image?: string | null;
  /** „Funktion · Firma“ aus dem Profil (leer, wenn nicht freigegeben) */
  headline?: string;
}

export interface Member extends UserLite {
  role: Role;
  /** Gast über Gastlink (kein eigenes Konto; E-Mail leer) */
  guest?: boolean;
  /** Über diesen Gastlink beigetreten (Zugang nur über den Link) */
  viaLinkId?: string | null;
  /** Kein Mitglied des Board-Teams (externer Gast mit eigenem Konto) */
  external?: boolean;
}

export interface Board {
  id: string;
  title: string;
  description: string | null;
  background: string | null;
  createdAt: string;
  updatedAt: string;
  role?: Role;
  /** Favorit der angemeldeten Person (nur in der Board-Liste) */
  favorite?: boolean;
  /** Team des Boards; null = ohne Team (Bestand) */
  teamId?: string | null;
  /** Name des Teams, nur wenn die Person Mitglied dieses Teams ist */
  teamName?: string | null;
  /** Notfallsperre: Board hat keinen aktiven Besitzer und ist schreibgeschützt */
  ownerMissingSince?: string | null;
  /** Ablage aus Sicht der angemeldeten Person (Board-Liste und Stand) */
  placement?: import('./structure.js').BoardPlacement;
  /** Ablageversion (ändert sich bei jedem Verschieben; Clients laden dann den Pfad neu) */
  placementVersion?: number;
  /** Archiviert seit (nur in der Archivliste) */
  archivedAt?: string | null;
  /** 0.67: Vorlage, aus der das Board entstand (Kennung und Fassung zum Zeitpunkt der Anlage) */
  sourceTemplate?: { id: string; version: number } | null;
  /** Kleines Erkennungszeichen in Übersicht und Boardwechsel (nur Board-Liste) */
  brand?: import('./branding.js').BoardBrand | null;
  /** 0.90 (K3): Beteiligte ohne Konto für dieses Board eingeschaltet */
  participantsEnabled?: boolean;
}

export interface Column {
  id: string;
  boardId: string;
  title: string;
  position: string;
  wipLimit: number | null;
  /** Spaltenregeln: Bedingung zum Hineinnehmen / zum Weitergeben */
  entryRule: string | null;
  exitRule: string | null;
  /** Auswertung: „noch nicht begonnen“ oder „in Arbeit“; null = Vorschlag (erste Spalte noch nicht begonnen) */
  flowStage?: 'backlog' | 'active' | null;
  /** 0.49: Farbe der Spalte – Labelfarbe, ab 0.51 auch `brand` (Markenfarbe des Boards) oder eigene `#rrggbb`; null = keine */
  color?: ColumnColor | null;
  /** 0.79: verbindliche Spaltenregeln (Hineinnehmen / Weitergeben) */
  entryGate?: ColumnGate | null;
  exitGate?: ColumnGate | null;
  /** 0.81: beim Hineinziehen nach Feldern fragen (Pflichtfelder in `required`) */
  entryAsk?: { fieldIds: string[]; required: string[] } | null;
  /** 0.81: Karten in dieser Spalte nur noch kommentieren (Besitzer*innen dürfen ändern) */
  locked?: boolean;
  /** 0.82: Summenlimit – Hinweis, wenn die Summe eines Feldes in der Spalte das Maximum übersteigt */
  sumLimit?: { fieldId: string; max: number } | null;
}

/** 0.79: Verbindliche Spaltenregel */
export interface ColumnGate {
  when: ConditionGroup;
  /** eigener Hinweis statt der Bedingungssätze */
  message?: string | null;
  /** Besitzer*innen dürfen mit Begründung übergehen */
  ownerOverride: boolean;
}

export interface Label {
  id: string;
  boardId: string;
  name: string;
  color: LabelColor;
}

/** Karte, wie sie auf dem Board erscheint */
export interface CardSummary {
  id: string;
  boardId: string;
  columnId: string;
  position: string;
  title: string;
  hasDescription: boolean;
  startAt: string | null;
  /** Start mit Uhrzeit (Zeitpunkt) oder ganztägig (Kalenderdatum) */
  startHasTime: boolean;
  dueAt: string | null;
  dueHasTime: boolean;
  priority: number;
  blocked: boolean;
  blockedReason: string | null;
  completedAt: string | null;
  cover: ResolvedCover | null;
  labelIds: string[];
  assigneeIds: string[];
  /** Hauptverantwortlich (eine der zuständigen Personen); weitere Zuständige sind Mitwirkende */
  ownerId: string | null;
  checklistTotal: number;
  checklistDone: number;
  attachmentCount: number;
  commentCount: number;
  recurrence: Recurrence | null;
  /** 0.66: Serie, zu der die Karte gehört (Wiederholung) */
  seriesId?: string | null;
  /** 0.66: Vorkommen (JJJJ-MM-TT) innerhalb der Serie */
  occurrenceKey?: string | null;
  /** Geschätzter Aufwand in Minuten */
  estimateMinutes: number | null;
  /** Erfasste Zeit in Minuten (inkl. laufender Timer) */
  spentMinutes: number;
  /** Personen, bei denen gerade ein Timer auf dieser Karte läuft */
  timerUserIds: string[];
  /** Werte eigener Felder: Feld-ID → Wert */
  custom: Record<string, unknown>;
  version: number;
  /** 0.48: zählt jede Änderung der Beschreibung (für `expectedDescriptionRev`) */
  descriptionRev: number;
  updatedAt: string;
  /** gesetzt = archiviert (nicht mehr auf dem Board) */
  archivedAt: string | null;
  /** Karten, auf die diese Karte wartet (Abhängigkeiten) */
  dependsOn: string[];
  /** davon archiviert, aber nicht erledigt – gelten weiter als offen (nicht im Board-Stand enthalten) */
  archivedBlockers?: number;
}

export interface ChecklistItem {
  id: string;
  cardId: string;
  text: string;
  done: boolean;
  position: string;
}

export interface Attachment {
  id: string;
  cardId: string;
  filename: string;
  mime: string;
  size: number;
  width: number | null;
  height: number | null;
  status: 'processing' | 'ready' | 'failed';
  isImage: boolean;
  url: string;
  thumbUrl: string | null;
  /** Großansicht: verkleinerte Variante (bis 2048 px) statt Original; Original bleibt für den Download */
  previewUrl?: string | null;
  previewWidth?: number | null;
  placeholder: string | null;
  uploaderId: string | null;
  /** Gehört zu diesem Kommentar (erscheint dort und unter „Dateien“) */
  commentId: string | null;
  createdAt: string;
  /** 0.58: Aus der Bibliothek, dort gibt es inzwischen eine neuere Fassung (nur in den Kartendetails) */
  libraryUpdate?: { fileId: string; version: number; team: boolean } | null;
  /** 0.89 (K2): Bedeutung der Unterlage an dieser Karte (Briefing, Referenz, Ergebnis) – gehört zur Zuordnung */
  relevance?: import('./context.js').AttachmentRelevance | null;
}

/** Anhang in der Medienübersicht eines Boards: mit Kartenbezug und Herkunft */
export interface MediaItem extends Attachment {
  cardTitle: string;
  cardArchived: boolean;
  uploaderName: string | null;
}

export interface CardDetail extends CardSummary {
  description: string | null;
  checklist: ChecklistItem[];
  attachments: Attachment[];
  createdAt: string;
  createdBy: string | null;
  /** Über ein Formular eingegangen (M03) */
  formOrigin?: { submissionId: string; formId: string; formTitle: string; number: string; at: string; contact: string | null; submitterName: string | null } | null;
  /** 0.66: Serie mit Regel (für die Zusammenfassung und den Dialog) */
  series?: CardSeries | null;
  /** 0.89 (K4): aktuelle Gründe, warum ein Schritt noch nicht geht (aus der Auswertung der Spaltenregeln) */
  hints?: import('./context.js').CardHint[];
}

export const FIELD_TYPES = [
  'text',
  'longtext',
  'number',
  'currency',
  'select',
  'multiselect',
  'date',
  'checkbox',
  'url',
  'email',
  'phone',
  'person',
  'rating',
  /** 0.80: Freigabe (Vier-Augen-Prinzip) – Wert nur über Freigeben/Ablehnen */
  'approval',
  /** 0.81: Laufende Nummer – vergibt KaBa beim Anlegen, nicht änderbar */
  'sequence',
  /** 0.82: Berechnet aus anderen Zahlenfeldern (Formel) */
  'formula',
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: 'Text',
  longtext: 'Langer Text',
  number: 'Zahl',
  currency: 'Betrag (€)',
  select: 'Auswahl',
  multiselect: 'Mehrfachauswahl',
  date: 'Datum',
  checkbox: 'Checkbox',
  url: 'Link',
  email: 'E-Mail',
  phone: 'Telefon',
  person: 'Person',
  rating: 'Bewertung (1–5)',
  approval: 'Freigabe',
  sequence: 'Laufende Nummer',
  formula: 'Berechnet (Formel)',
};

export interface FieldOption {
  id: string;
  label: string;
  color: LabelColor;
}

export interface CustomField {
  id: string;
  boardId: string;
  name: string;
  type: FieldType;
  options: FieldOption[];
  position: string;
  showOnCard: boolean;
  /** In den Kartendetails unter „Übersicht“ angeheftet */
  pinned: boolean;
  /** 0.80: Einstellungen je Feldtyp (Freigabe; ab 0.82 Einheit, Auswertung, Formel) */
  config?: FieldConfig | null;
}

/** 0.80: Einstellungen eines eigenen Feldes */
export interface FieldConfig {
  approval?: import('./approval.js').ApprovalConfig | null;
  /** 0.81: Laufende Nummer */
  sequence?: SequenceConfig | null;
  /** 0.81: Zählerstand der laufenden Nummer (nur Server; nie aus Anfragen übernommen) */
  counter?: { n: number; year: number } | null;
  /** 0.82: Auswertung (Spaltenkopf, Tabelle, Zeilen, PDF); null = keine */
  aggregate?: import('./aggregate.js').AggregateKind | null;
  /** 0.82: Einheit (Zahl, Formel) und Nachkommastellen */
  unit?: string | null;
  decimals?: number | null;
  /** 0.82: Währung eines Betrags (ISO, Standard EUR) */
  currency?: string | null;
  /** 0.82: Formel eines berechneten Feldes */
  formula?: import('./aggregate.js').FormulaConfig | null;
}

/** 0.81: Laufende Nummer – „R-{JJJJ}-“ + Nummer mit `digits` Stellen; `yearly`: jedes Jahr ab 1 */
export interface SequenceConfig {
  prefix: string;
  digits: number;
  yearly: boolean;
}

export interface Comment {
  id: string;
  cardId: string;
  authorId: string | null;
  /** Inhalt je nach `format`: 1 = reiner Text, 2 = HTML (siehe commentContent.ts) */
  body: string;
  format: 1 | 2;
  mentionIds: string[];
  /** Bilder und Dateien des Kommentars */
  attachments: Attachment[];
  createdAt: string;
  editedAt: string | null;
}

export type ActivityType =
  | 'created'
  | 'moved'
  | 'completed'
  | 'reopened'
  | 'due'
  | 'assigned'
  | 'unassigned'
  | 'attachment'
  | 'blocked'
  | 'unblocked'
  | 'archived'
  | 'restored'
  | 'renamed'
  | 'owner'
  | 'automation'
  | 'dependency'
  | 'series'
  /** 0.79: eigenes Feld geändert (`fieldId`, `name`, `from`, `to`; `via` = Automation) */
  | 'field'
  /** 0.79: verbindliche Spaltenregel übergangen (`column`, `side`, `reason`, `rule`) */
  | 'gateOverride'
  /** 0.89: beschreibende Beziehung (`added`/`removed`: Satz + Titel der anderen Karte) */
  | 'relation'
  /** 0.89: Relevanz einer Unterlage (`filename`, `relevance`) */
  | 'relevance'
  /** 0.90: Beteiligung ohne Konto (`action` added/changed/removed, `role`, `participantId` – Name wird live aufgelöst) */
  | 'participation';

export interface Activity {
  id: number;
  cardId: string;
  actorId: string | null;
  type: ActivityType;
  data: Record<string, unknown>;
  createdAt: string;
}

export type NotificationType = 'assigned' | 'mentioned' | 'comment' | 'invited' | 'due' | 'automation' | 'reminder' | 'handover' | 'form';

export interface Notification {
  id: number;
  type: NotificationType;
  boardId: string | null;
  boardTitle: string | null;
  cardId: string | null;
  cardTitle: string | null;
  /** Kommentar, um den es geht (Erwähnung, neuer Kommentar) */
  commentId: string | null;
  actorName: string | null;
  text: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface BoardSnapshot {
  board: Board;
  columns: Column[];
  cards: CardSummary[];
  labels: Label[];
  fields: CustomField[];
  members: Member[];
  /** expiresAt null = alte Einladung ohne Link (neu einladen) */
  invitations: { email: string; role: Role; expiresAt: string | null }[];
  /** Gespeicherte Ansichten: geteilte + eigene */
  views: SavedView[];
  /** Kartenvorlagen des Boards */
  cardTemplates: CardTemplate[];
  /** Aufgelöstes Erscheinungsbild (Installation → Team → Board) */
  branding: import('./branding.js').ResolvedBranding;
  /** 0.89 (K2): beschreibende Beziehungen zwischen Karten dieses Boards */
  relations?: import('./context.js').CardRelation[];
  /** 0.89: archivierte Karten, auf die eine Beziehung zeigt (nur Titel) */
  relationCards?: { id: string; title: string }[];
  /** 0.90 (K3): Beteiligte ohne Konto und Beteiligungen (nur wenn am Board eingeschaltet) */
  participants?: import('./context.js').BoardParticipant[];
  participations?: import('./context.js').CardParticipation[];
  lastEventId: number;
}

export type BoardEventType =
  | 'board.updated'
  | 'column.upserted'
  | 'column.deleted'
  | 'card.upserted'
  | 'card.deleted'
  | 'label.upserted'
  | 'label.deleted'
  | 'field.upserted'
  | 'field.deleted'
  | 'comment.changed'
  | 'members.changed'
  | 'view.upserted'
  | 'view.deleted'
  | 'templates.changed'
  /** 0.54: Dateibibliothek geändert (Ansicht neu laden) */
  | 'library.changed'
  | 'automations.changed'
  | 'branding.changed'
  /** 0.89: Beziehung zwischen Karten angelegt/entfernt, Board-Kontext geändert */
  | 'relation.upserted'
  | 'relation.deleted'
  | 'context.changed'
  /** 0.90 (K3): Beteiligte ohne Konto und ihre Beteiligungen an Karten */
  | 'participant.upserted'
  | 'participant.deleted'
  | 'participation.upserted'
  | 'participation.deleted';

export interface BoardEvent {
  id: number;
  boardId: string;
  type: BoardEventType;
  actorId: string | null;
  opId: string | null;
  entityId: string | null;
  payload: unknown;
  createdAt: string;
}

/** Nachrichten über den WebSocket */
export type ClientMessage =
  | { type: 'subscribe'; boardId: string; after: number }
  | { type: 'unsubscribe'; boardId: string }
  /** Präsenz: welche Karte habe ich in diesem Board gerade offen (null = keine) */
  | { type: 'presence'; boardId: string; cardId: string | null }
  /** Übersicht offen: über Kartenänderungen in eigenen Boards informieren (ohne Inhalte) */
  | { type: 'watch-cards'; on: boolean }
  | { type: 'ping' }
  /* 0.48: Beschreibung gemeinsam bearbeiten (Yjs; Daten als Base64) */
  /** Raum betreten; `docId`/`sv` = eigener Stand nach einem Verbindungsabbruch */
  | { type: 'y-join'; cardId: string; docId: string | null; sv: string | null }
  | { type: 'y-leave'; cardId: string }
  /** `seq` zählt die eigenen Änderungen (für „gespeichert“); `seed` = Anfangsinhalt aus dem gespeicherten HTML */
  | { type: 'y-update'; cardId: string; update: string; seq: number; seed?: boolean }
  | { type: 'y-awareness'; cardId: string; update: string }
  /** Jetzt speichern (z. B. bei „Fertig“) */
  | { type: 'y-flush'; cardId: string };

export interface PresenceEntry {
  userId: string;
  /** Karten, die die Person gerade offen hat (mehrere Tabs möglich) */
  cardIds: string[];
  /** 0.48: Karten, deren Beschreibung die Person gerade bearbeitet */
  editing?: string[];
}

export type ServerMessage =
  | { type: 'event'; event: BoardEvent }
  | { type: 'resync'; boardId: string }
  | { type: 'subscribed'; boardId: string; lastEventId: number }
  | { type: 'notification'; notification: Notification }
  | { type: 'presence'; boardId: string; users: PresenceEntry[] }
  | { type: 'error'; message: string }
  /** Bereiche/Ordner haben sich geändert: Navigation neu laden */
  | { type: 'structure' }
  /** In einem zugänglichen Board hat sich etwas geändert (Übersicht neu laden) */
  | { type: 'cards-changed' }
  | { type: 'pong' }
  /**
   * 0.48: im Raum. `seed` = du legst den Anfangsinhalt aus `html` an; sonst `update` = was dir fehlt.
   * `fresh` = dein bisheriger Stand gehört zu einem anderen Dokument (verwerfen und neu beginnen).
   */
  | { type: 'y-joined'; cardId: string; docId: string; seed: boolean; html: string | null; update: string | null; fresh: boolean; awareness: string | null }
  | { type: 'y-update'; cardId: string; update: string }
  | { type: 'y-awareness'; cardId: string; update: string }
  /** Gespeichert bis einschließlich deiner Änderung `seq` */
  | { type: 'y-saved'; cardId: string; seq: number }
  /** Die Beschreibung wurde anders ersetzt (z. B. offline gespeicherte Fassung): neu betreten */
  | { type: 'y-reset'; cardId: string }
  | { type: 'y-error'; cardId: string; message: string; fatal: boolean };

/* ---------- v0.3: Instanz-Einstellungen ---------- */

export type SmtpSecurity = 'none' | 'starttls' | 'tls';

export interface SmtpSettingsView {
  enabled: boolean;
  host: string;
  port: number;
  security: SmtpSecurity;
  username: string;
  /** Passwort wird nie ausgeliefert – nur ob eines gespeichert ist */
  hasPassword: boolean;
  /** Gespeichertes Passwort lässt sich nicht entschlüsseln (z. B. KABA_SECRET geändert) */
  passwordUnreadable: boolean;
  fromEmail: string;
  fromName: string;
  replyTo: string;
  allowSelfSigned: boolean;
  /** Woher die aktiven Werte kommen */
  source: 'db' | 'env' | 'none';
}

export interface GeneralSettings {
  instanceName: string;
  signup: 'open' | 'invite';
  /** Webhooks dürfen Adressen im internen Netz (localhost, 10.x, 192.168.x …) aufrufen */
  webhooksAllowPrivate: boolean;
}

export interface AdminSettings {
  general: GeneralSettings;
  smtp: SmtpSettingsView;
  publicUrl: string;
  version: string;
  /** null = kein Backup-Container eingebunden */
  backup: BackupStatus | null;
  oidc: OidcSettingsView;
  /** 0.78: Virenprüfung für öffentliche Uploads (clamd über KABA_CLAMAV) */
  scanner: ScannerStatus;
  /** 0.91: Office-Vorschau (LibreOffice im Server-Image) */
  office: { available: boolean; version: string | null };
}

/** 0.78: Zustand der Virenprüfung – nicht eingerichtet, erreichbar (mit Signaturstand) oder nicht erreichbar */
export type ScannerStatus =
  | { state: 'off' }
  | { state: 'ok'; address: string; version: string | null; signatures: string | null }
  | { state: 'down'; address: string; error: string };

/** Inhalt von /backups/status.json (vom Backup-Container geschrieben) */
export interface BackupStatus {
  ok: boolean;
  /** ISO-Zeitpunkt der letzten Sicherung */
  time: string | null;
  database?: string;
  databaseBytes?: number;
  files?: string;
  filesBytes?: number;
  keepDays?: number;
  schedule?: string;
  error?: string;
}

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  isAdmin: boolean;
  createdAt: string;
  boardCount: number;
}

export interface MailLogEntry {
  id: number;
  to: string;
  subject: string;
  status: 'pending' | 'sent' | 'failed' | 'cancelled';
  attempts: number;
  lastError: string | null;
  createdAt: string;
  sentAt: string | null;
}

export interface EmailPrefs {
  mentioned: boolean;
  assigned: boolean;
  comment: boolean;
  due: boolean;
  invited: boolean;
  automation: boolean;
  /** Erinnerungen aus Regeln (Frist, Start, Termin, Eskalation) */
  reminder: boolean;
  /** Übergaben: Aufgabe ist startbereit */
  handover: boolean;
  /** Neue Einsendung über ein Formular */
  form: boolean;
}

export const DEFAULT_EMAIL_PREFS: EmailPrefs = {
  mentioned: true,
  assigned: true,
  comment: false,
  due: true,
  invited: true,
  automation: true,
  reminder: true,
  handover: true,
  form: true,
};

export interface Me extends UserLite {
  isAdmin: boolean;
  /** Gast über Gastlink (kein eigenes Konto) */
  isGuest: boolean;
  /** @deprecated – siehe notifyPrefs.email */
  emailPrefs: EmailPrefs;
  notifyPrefs: NotifyPrefs;
  /** Persönliche Darstellung (Hell/Dunkel/System, dekorative Bilder) */
  appearance: import('./branding.js').Appearance;
  /** Persönliche Menüsichtbarkeit; ältere Server liefern sie noch nicht. */
  navigationPrefs?: import('./navigationPrefs.js').NavigationPrefs;
  /** Freiwillige Profilangaben */
  profile: import('./profile.js').UserProfile;
}

/* ---------- v0.4: Wiederholung und Zeiterfassung ---------- */

export interface Recurrence {
  freq: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number;
  /** due = nächste Frist ab alter Frist (Kalender); completion = ab Erledigung */
  basis: 'due' | 'completion';
  /**
   * Prüfbericht 0.63, R01: Ankertag (1–31) monatlicher/jährlicher Serien. Fehlt er in einem Monat (31. im
   * Februar), wird auf den letzten Tag gekürzt – die Folgetermine richten sich aber wieder nach dem Anker.
   */
  anchorDay?: number;
}

export const RECURRENCE_LABEL: Record<Recurrence['freq'], [string, string]> = {
  daily: ['Täglich', 'Tage'],
  weekly: ['Wöchentlich', 'Wochen'],
  monthly: ['Monatlich', 'Monate'],
  yearly: ['Jährlich', 'Jahre'],
};

export function describeRecurrence(r: Recurrence): string {
  const [single, plural] = RECURRENCE_LABEL[r.freq];
  const base = r.interval === 1 ? single : `Alle ${r.interval} ${plural}`;
  if (r.basis === 'completion') return `${base} nach Erledigung`;
  // R01: Ankertag sichtbar machen (kürzere Monate: letzter Tag)
  if (r.freq === 'monthly' && r.anchorDay) return `${base} am ${r.anchorDay}.${r.anchorDay > 28 ? ' (sonst am Monatsletzten)' : ''}`;
  return base;
}

/**
 * Nächster Termin: Monatsenden werden auf den letzten Tag gekürzt (31. → 30./28.). Mit `anchorDay` zählt der
 * ursprüngliche Kalendertag, nicht der zuletzt gekürzte (31.01. → 28.02. → 31.03.; 29.02. bleibt im Schaltjahr).
 */
export function nextOccurrence(from: Date, r: Recurrence): Date {
  const d = new Date(from);
  const n = Math.max(1, r.interval);
  if (r.freq === 'daily') d.setDate(d.getDate() + n);
  else if (r.freq === 'weekly') d.setDate(d.getDate() + 7 * n);
  else {
    const day = r.anchorDay ?? d.getDate();
    d.setDate(1);
    d.setMonth(d.getMonth() + (r.freq === 'monthly' ? n : 12 * n));
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(day, last));
  }
  return d;
}

export interface TimeEntry {
  id: string;
  cardId: string;
  userId: string;
  startedAt: string;
  endedAt: string | null;
  minutes: number;
  note: string | null;
}

export interface RunningTimer {
  entryId: string;
  cardId: string;
  cardTitle: string;
  boardId: string;
  startedAt: string;
}

export interface MyCard extends CardSummary {
  boardTitle: string;
  columnTitle: string;
}

export function formatMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (!h) return `${m} Min.`;
  return m ? `${h} Std. ${m} Min.` : `${h} Std.`;
}

/* ---------- v0.7: Flow-Kennzahlen ---------- */

export interface DurationStats {
  /** Anzahl Karten in der Auswertung */
  count: number;
  /** Tage */
  median: number | null;
  p85: number | null;
}

export interface BoardMetrics {
  days: number;
  generatedAt: string;
  /** Beginn des ausgewerteten Zeitraums (ISO) und Zeitzone der Kalenderwochen */
  from: string;
  timeZone: string;
  /** Start der Arbeit → erledigt; `unknown`: erledigt, aber Arbeitsbeginn nicht belegt */
  cycleTime: DurationStats & { unknown: number };
  /** Angelegt → erledigt */
  leadTime: DurationStats;
  /**
   * Erledigte Karten je Kalenderwoche über den ganzen Zeitraum (älteste zuerst).
   * count null = vor Anlage des Boards (unbekannt, nicht 0); partial = Woche nur teilweise im Zeitraum.
   */
  throughput: { weekStart: string; label: string; count: number | null; partial: boolean }[];
  /** Im Zeitraum erledigt (Haken gesetzt, letzter Abschluss im Zeitraum) */
  completed: number;
  /** Im Zeitraum erledigt und inzwischen wieder geöffnet – separat, nicht im Durchsatz */
  reopened: number;
  /** Offene Karten in der letzten Spalte ohne Erledigt-Haken (zählen als offen) */
  openInLastColumn: number;
  /** Offene, begonnene Karten: Alter seit Start, älteste zuerst (max. 10) */
  oldestOpen: { cardId: string; title: string; columnId: string; ageDays: number; blocked: boolean }[];
  openStarted: number;
  openNotStarted: number;
  /** Wirksame Auswertungsstufe je Spalte */
  stages: Record<string, 'backlog' | 'active'>;
  /** Durchschnittliche Verweildauer erledigter Karten je Spalte (Tage) */
  timeInColumn: { columnId: string; title: string; avgDays: number; cards: number }[];
  /** Aktuelle Belegung je Spalte (offene Karten) */
  wip: { columnId: string; title: string; count: number; limit: number | null }[];
  blocked: {
    /** Summe der Blockadezeit im Zeitraum (Tage) */
    totalDays: number;
    /** Karten, die im Zeitraum blockiert waren */
    cards: number;
    current: { cardId: string; title: string; sinceDays: number; reason: string | null }[];
  };
}

/* ---------- v0.9: Anbindung ---------- */

/** 0.73: Inhalt eines Kalender-Abos: nur Fristen, Start und Frist als einzelne Termine, Zeitraum als Block */
export type CalendarFeedDates = 'due' | 'separate' | 'span';
export const CALENDAR_FEED_DATES: { value: CalendarFeedDates; label: string; hint: string }[] = [
  { value: 'due', label: 'Nur Fristen', hint: 'Ein Termin am Tag der Frist.' },
  { value: 'separate', label: 'Start und Frist einzeln', hint: 'Je ein kurzer Termin für den Start und für die Frist – kein durchgehender Block.' },
  { value: 'span', label: 'Zeitraum als Block', hint: 'Ein Termin von Start bis Frist, z. B. für mehrtägige Einsätze.' },
];

export interface CalendarFeed {
  id: string;
  /** null = „Meine Fristen“ über alle Boards */
  boardId: string | null;
  boardTitle: string | null;
  dates: CalendarFeedDates;
  createdAt: string;
  lastUsedAt: string | null;
}

/** Nur direkt nach dem Anlegen: der Link (wird nicht gespeichert) */
export interface CalendarFeedCreated extends CalendarFeed {
  url: string;
  webcalUrl: string;
}

export interface ApiTokenInfo {
  id: string;
  name: string;
  /** Anfang des Tokens zum Wiedererkennen */
  prefix: string;
  scope: 'read' | 'write';
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
}

export interface WebhookInfo {
  id: string;
  boardId: string;
  url: string;
  events: string[];
  enabled: boolean;
  failCount: number;
  lastStatus: number | null;
  lastError: string | null;
  lastDeliveryAt: string | null;
  nextAttemptAt: string | null;
  createdAt: string;
}

export interface WebhookDeliveryInfo {
  id: number;
  eventSeq: number | null;
  eventType: string;
  status: number | null;
  error: string | null;
  durationMs: number | null;
  at: string;
}

/* ---------- v0.10: Gastzugang ---------- */

export type GuestRole = 'viewer' | 'commenter' | 'editor';

export interface ShareLinkInfo {
  id: string;
  boardId: string;
  name: string;
  role: GuestRole;
  url: string;
  expiresAt: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  /** Wie viele Personen über diesen Link im Board sind */
  memberCount: number;
}

/** Öffentliche Vorschau eines Gastlinks */
export interface ShareLinkPreview {
  boardTitle: string;
  role: GuestRole;
  instanceName: string;
  /** Angemeldete Person (Konto oder Gast) – null, wenn niemand angemeldet ist */
  me: { name: string; isGuest: boolean } | null;
  /** Bereits Mitglied → direkt ins Board */
  member: boolean;
  boardId: string | null;
  /** Logo und Farbe des Boards – erst nach Prüfung des Links */
  brand?: import('./branding.js').EntryBrand | null;
}

/* ---------- v0.11: Anmeldung über OIDC, Passkeys ---------- */

export interface OidcSettingsView {
  enabled: boolean;
  /** Beschriftung des Knopfs, z. B. „Mit Microsoft anmelden“ */
  label: string;
  /** Aussteller-Adresse (Issuer) oder direkt die .well-known/openid-configuration */
  issuer: string;
  clientId: string;
  hasSecret: boolean;
  secretUnreadable: boolean;
  scopes: string;
  /** Neue Konten beim ersten Anmelden automatisch anlegen */
  autoCreate: boolean;
  /** Nur diese Domains dürfen neue Konten anlegen (leer = alle) */
  allowedDomains: string;
  /** Passwort-Anmeldung auf der Startseite ausblenden (Notzugang über /?passwort) */
  hidePassword: boolean;
  /** Zum Eintragen beim Anbieter */
  callbackUrl: string;
}

/** Öffentliche Angaben für die Anmeldeseite */
export interface LoginOptions {
  oidc: { label: string } | null;
  passwordLogin: boolean;
  passkeys: boolean;
}

export interface PasskeyInfo {
  id: string;
  name: string | null;
  createdAt: string | null;
  deviceType: string;
  backedUp: boolean;
}
